import { getAppConfig } from '@services/config';

import { deliveryRepository } from '@repositories/delivery';

import { SapiProductChildType } from '@app-types/api/sapi';
import { ProductStockDetails } from '@app-types/product';
import { DeliveryContextType, DeliveryModesType, StockType } from '@app-types/product-delivery';

import { SHIPPING_MODES, STOCK_STATUS, StockConfigType } from '@constants/stock';
import { MILLIS_SECONDS_IN_A_DAY } from '@constants/time';

const appConf = getAppConfig();

export function getStockStatus(
    isEresa: boolean,
    isPreorder: boolean,
    isInStock: boolean,
    isAvailableSoon?: boolean,
    isSoonExhausted?: boolean,
): STOCK_STATUS {
    switch (true) {
        // Pré-commande
        case isPreorder && isInStock:
            return STOCK_STATUS.PRE_ORDER;
        // Pas de stock && e-resa => Uniquement boutique
        case !isInStock && isEresa:
            return STOCK_STATUS.ONLY_IN_SHOP;
        // Pas de e-resa + config BO bientot dispo ==> bientôt disponible
        case !isInStock && !isEresa && isAvailableSoon:
            return STOCK_STATUS.SOON_AVAILABLE;
        // Bientôt épuisé
        case isSoonExhausted:
            return STOCK_STATUS.SOON_OUT_OF_STOCK;
        // Pas de stock
        case !isInStock:
            return STOCK_STATUS.OUT_OF_STOCK;
        default:
            return STOCK_STATUS.IN_STOCK;
    }
}

function getClosestDeliveryDate(deliveryModes: DeliveryModesType): string | undefined {
    const closestDeliveryDate = deliveryModes
        .filter((mode) => [SHIPPING_MODES.LA_POSTE, SHIPPING_MODES.CHRONOPOST, SHIPPING_MODES.DIRECT].includes(mode.id))
        .map((mode) => new Date(mode.estimatedDeliveryDate))
        .sort((a, b) => a.getTime() - b.getTime())[0];

    if (!closestDeliveryDate) {
        return undefined;
    }

    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

    const now = new Date();
    const time = closestDeliveryDate.getTime() - now.getTime();

    switch (true) {
        case time < MILLIS_SECONDS_IN_A_DAY:
            return 'dès demain';
        case time < MILLIS_SECONDS_IN_A_DAY * 6:
            return `à partir de ${days[closestDeliveryDate.getDay()]}`;
        default:
            const day = `0${closestDeliveryDate.getDate()}`.slice(-2);
            const month = `0${closestDeliveryDate.getMonth() + 1}`.slice(-2);
            return `à partir du ${day}/${month}`;
    }
}

export async function getStockOptions(
    stock: StockConfigType,
    child: SapiProductChildType,
    productStock: StockType,
    deliveryContext?: DeliveryContextType,
): Promise<Omit<ProductStockDetails, 'stock'>> {
    let deliveryModes: DeliveryModesType = [];
    let closestShippingDate: string | undefined;
    let canFreeShipping = false;

    if (stock.hasShipping) {
        deliveryModes = await deliveryRepository.getSimulatedDeliveryModes(child.gencode, deliveryContext);
        if (deliveryModes.length > 0) {
            canFreeShipping = deliveryModes.some(
                (mode) => mode.id !== SHIPPING_MODES.CLICK_AND_COLLECT && mode.price === 0,
            );
            closestShippingDate = getClosestDeliveryDate(deliveryModes);
        }
    }

    const hasClickAndCollect = !!child.available_click_and_collect && !child.exclu_web && stock.hasCnC;

    return {
        closestShippingDate,
        hasClickAndCollect,
        hasFreeShipping: !hasClickAndCollect && canFreeShipping && stock.hasStock,
        isAvailableInStore: !child.exclu_web && (!child.in_stock || child.qty === 0) && productStock.isEresa,
        hasStockInStore: child.qty > 0 && child.in_stock && !child.exclu_web && !productStock.isPreresa,
        isPreresa: productStock.isPreresa,
        isPreorder: child.is_preorder,
        isInStock: child.in_stock,
        isSoonExhausted: child.soon_exhausted,
        isAvailableSoon: child.available_soon,
    };
}

export function isOutOfStock(status: STOCK_STATUS) {
    return [STOCK_STATUS.OUT_OF_STOCK, STOCK_STATUS.ONLY_IN_SHOP, STOCK_STATUS.SOON_AVAILABLE].includes(status);
}

export function getPreorderEresaUrl(gencode: string, campaignId: string): string {
    return `${appConf.eresa.preorder}/${campaignId}/step-store?productId=${gencode}&headerFooter=0&origin=web`;
}

export function getStoreLocatorEresaUrl(gencode: string): string {
    return `${appConf.eresa.locator}?productId=${gencode}&isProductLocator=1&headerFooter=0&origin=web`;
}
